import { z } from "@/zod";
import { ulid, dateTime, postalCode, apartmentNumber } from "./commonFields";
import { AGREEMENT_STATUS } from "@/types/agreement";
import { format } from "date-fns";
import { DEFAULT_DATE_FORMAT } from "@/constants";
import i18n from "@/i18n";
import {
  questionnaireDataBase,
  questionnaireStatementsBase,
  questionnaireAttachments,
  fileMetadata,
} from "./questionnaire";

export const agreement = z.object({
  id: ulid,
  agreement_ref_number: z.number(),
  contractor_user_ref_number: z.string().nullable().optional(),
  contractor_full_name: z.string(),
  contractor_last_name: z.string().nullable().optional(),
  contractor_first_name: z.string().nullable().optional(),
  agreement_type_name: z.string(),
  agreement_company_name: z.string(),
  contractor_phone_number: z.string(),
  contractor_email: z.string(),
  contractor_personal_id_number: z.string(),
  agreement_status: z.enum(AGREEMENT_STATUS),
  statements_up_to_date: z.boolean().nullable().optional(),
  statements_confirmed_by_contractor_at: dateTime.nullable().optional(),
  contractor_registered_by: ulid.nullable().optional(),
  contractor_registered_by_name: z.string().nullable().optional(),
  agreement_start_date: z.string().date(),
  agreement_sign_date: z.string().date().nullable().optional(),
  agreement_end_date: z.string().date().nullable().optional(),
  agreement_ended_at: dateTime.nullable().optional(),
  city: z.string().nullable().optional(),
  postal_code: z.string().nullable().optional(),
  post_office: z.string().nullable().optional(),
  street: z.string().nullable().optional(),
  house_number: z.string().nullable().optional(),
  apartment_number: z.string().nullable().optional(),
  province_name: z.string().nullable().optional(),
  province_nfz_code: z.string().nullable().optional(),
  county: z.string().nullable().optional(),
  municipality: z.string().nullable().optional(),
  birth_date: z.string().date().nullable().optional(),
  birth_place: z.string().nullable().optional(),
  gender: z.string().nullable().optional(),
  bank_account_number: z.string().nullable().optional(),
  tax_office_name: z.string().nullable().optional(),
  citizenship_name: z.string().nullable().optional(),
  disability_certificate_code: z.string().nullable().optional(),
  rodo_accepted: z.boolean().nullable().optional(),
  regulations_accepted: z.boolean().nullable().optional(),
  is_student: z.boolean().nullable().optional(),
  school_name: z.string().nullable().optional(),
  student_id_number: z.string().nullable().optional(),
  student_id_valid_until: z.string().date().nullable().optional(),
  is_employed: z.boolean().nullable().optional(),
  employer_name: z.string().nullable().optional(),
  employment_certificate_issue_date: z.string().date().nullable().optional(),
  voluntary_sickness_insurance: z.boolean().nullable().optional(),
  is_pensioner: z.boolean().nullable().optional(),
  pension_number: z.string().nullable().optional(),
  pension_start_date: z.string().date().nullable().optional(),
  pension_office_name: z.string().nullable().optional(),
  is_disability_pensioner: z.boolean().nullable().optional(),
  disability_pension_number: z.string().nullable().optional(),
  disability_pension_start_date: z.string().date().nullable().optional(),
  disability_pension_office_name: z.string().nullable().optional(),
  disability_pension_type: z.string().nullable().optional(),
  has_valid_visa: z.boolean().nullable().optional(),
  visa_number: z.string().nullable().optional(),
  visa_valid_until: z.string().date().nullable().optional(),
  has_polish_card: z.boolean().nullable().optional(),
  polish_card_valid_until: z.string().date().nullable().optional(),
  has_residence_card: z.boolean().nullable().optional(),
  residence_card_valid_until: z.string().date().nullable().optional(),
  watched_instructional_videos: z.boolean().nullable().optional(),
  is_b2b_agreement: z.boolean().nullable().optional(),
  agreement_tax_status: z.string().nullable().optional(),
  recruitment_responsible_user_full_name: z.string().nullable().optional(),
  hr_department_responsible_user_full_name: z.string().nullable().optional(),
  deleted_at: dateTime.nullable().optional(),
  payroll_comment: z.string().nullable().optional(),
});

export const readAgreement = agreement.readonly();

const agreementFormBase = z.object({
  type_id: ulid,
  contractor_id: ulid,
  start_date: z.string().date(),
  agreementType: z
    .object({
      valid_from: z.string().optional(),
      valid_to: z.string().optional(),
    })
    .optional(),
});

export const agreementFormSchema = agreementFormBase.superRefine(
  (data, ctx) => {
    if (data.agreementType && data.start_date) {
      const startDate = new Date(data.start_date);
      const validFrom = data.agreementType.valid_from
        ? new Date(data.agreementType.valid_from)
        : null;
      const validTo = data.agreementType.valid_to
        ? new Date(data.agreementType.valid_to)
        : null;

      if (validFrom && startDate < validFrom) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t("agreements.errors.startDateBeforeValidFrom", {
            validFrom: format(validFrom, DEFAULT_DATE_FORMAT),
          }),
          path: ["start_date"],
        });
      }

      if (validTo && startDate > validTo) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: i18n.t("agreements.errors.startDateAfterValidTo", {
            validTo: format(validTo, DEFAULT_DATE_FORMAT),
          }),
          path: ["start_date"],
        });
      }
    }
  },
);

export const writeAgreement = agreementFormBase.pick({
  type_id: true,
  contractor_id: true,
  start_date: true,
});

// Tax status changed info schema
const taxStatusChangedInfo = z.object({
  parallel_agreement_id: ulid,
  parallel_agreement_ref_number: z.number(),
  parallel_agreement_tax_status: z.string(),
  new_tax_status: z.string(),
});

// B2B data schema
const b2bQuestionnaireBase = z.object({
  company_tax_identification_number: z.string().nullable(),
  company_name: z.string().nullable(),
  company_postal_code: postalCode.nullable(),
  company_city: z.string().nullable(),
  company_street_name: z.string().nullable(),
  company_street_number: z.string().nullable(),
  company_apartment_number: apartmentNumber.nullish(),
});

export const agreementDetail = z.object({
  agreement_id: ulid,
  agreement_ref_number: z.number(),
  agreement_status: z.enum(AGREEMENT_STATUS),
  agreement_start_date: z.string().date(),
  agreement_sign_date: z.string().date().nullable(),
  agreement_end_date: z.string().date().nullable(),
  agreement_ended_at: dateTime.nullable().optional(),
  agreement_accepted_by_contractor_at: dateTime.nullable(),
  agreement_type_id: ulid,
  agreement_type_name: z.string(),
  agreement_company_id: ulid,
  agreement_company_name: z.string(),
  agreement_company_full_name: z.string(),
  agreement_last_amendment_number: z.number(),
  agreement_last_amendment_date: z.string().date().nullable(),
  agreement_template_code: z.string(),
  questionnaire_id: ulid,
  questionnaire_is_draft: z.boolean(),
  agreement_statements_confirmed_by_contractor_at: dateTime.nullable(),
  agreement_statements_up_to_date: z.boolean().nullable(),
  is_b2b_agreement: z.boolean(),
  agreement_tax_status: z.string().nullable().optional(),
  recruitment_responsible_user_full_name: z.string().nullable().optional(),
  hr_department_responsible_user_full_name: z.string().nullable().optional(),
});

export const contractorDetail = z.object({
  contractor_id: ulid,
  contractor_user_id: ulid,
  contractor_user_ref_number: z.string(),
  contractor_full_name: z.string(),
  contractor_last_name: z.string().nullable().optional(),
  contractor_first_name: z.string().nullable().optional(),
  contractor_email: z.string(),
  contractor_phone_number: z.string(),
  contractor_personal_id_number: z.string().nullable().optional(),
  contractor_registered_by: ulid.nullable().optional(),
  contractor_registered_by_name: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  postal_code: z.string().nullable().optional(),
  post_office: z.string().nullable().optional(),
  street: z.string().nullable().optional(),
  house_number: z.string().nullable().optional(),
  apartment_number: z.string().nullable().optional(),
  province_name: z.string().nullable().optional(),
  province_nfz_code: z.string().nullable().optional(),
  county: z.string().nullable().optional(),
  municipality: z.string().nullable().optional(),
  birth_date: z.string().date().nullable().optional(),
  birth_place: z.string().nullable().optional(),
  gender: z.string().nullable().optional(),
  bank_account_number: z.string().nullable().optional(),
  tax_office_name: z.string().nullable().optional(),
  citizenship_name: z.string().nullable().optional(),
  disability_certificate_code: z.string().nullable().optional(),
  rodo_accepted: z.boolean().nullable().optional(),
  regulations_accepted: z.boolean().nullable().optional(),
  is_student: z.boolean().nullable().optional(),
  school_name: z.string().nullable().optional(),
  student_id_number: z.string().nullable().optional(),
  student_id_valid_until: z.string().date().nullable().optional(),
  is_employed: z.boolean().nullable().optional(),
  employer_name: z.string().nullable().optional(),
  employment_certificate_issue_date: z.string().date().nullable().optional(),
  voluntary_sickness_insurance: z.boolean().nullable().optional(),
  is_pensioner: z.boolean().nullable().optional(),
  pension_number: z.string().nullable().optional(),
  pension_start_date: z.string().date().nullable().optional(),
  pension_office_name: z.string().nullable().optional(),
  is_disability_pensioner: z.boolean().nullable().optional(),
  disability_pension_number: z.string().nullable().optional(),
  disability_pension_start_date: z.string().date().nullable().optional(),
  disability_pension_office_name: z.string().nullable().optional(),
  disability_pension_type: z.string().nullable().optional(),
  has_valid_visa: z.boolean().nullable().optional(),
  visa_number: z.string().nullable().optional(),
  visa_valid_until: z.string().date().nullable().optional(),
  has_polish_card: z.boolean().nullable().optional(),
  polish_card_valid_until: z.string().date().nullable().optional(),
  has_residence_card: z.boolean().nullable().optional(),
  residence_card_valid_until: z.string().date().nullable().optional(),
  watched_instructional_videos: z.boolean().nullable().optional(),
});

const agreementComment = z.object({
  id: ulid,
  comment: z.string(),
  created_at: dateTime,
  created_by: ulid,
  created_by_name: z.string(),
  created_by_role_name: z.string(),
});

const agreementHistoryEntry = z.object({
  action: z.string(),
  user_id: ulid,
  user_full_name: z.string(),
  user_role_name: z.string(),
  updated_at: dateTime,
});

export const agreementDetailResponse = z.object({
  agreement: agreementDetail,
  contractor: contractorDetail,
  draft: questionnaireDataBase,
  non_draft: questionnaireDataBase,
  draft_statements: questionnaireStatementsBase,
  statements: questionnaireStatementsBase,
  draft_b2b: b2bQuestionnaireBase,
  b2b: b2bQuestionnaireBase,
  questionnaire_attachments: questionnaireAttachments,
  agreement_files: z.array(fileMetadata),
  comments: z.array(agreementComment),
  allowed_new_statuses: z.array(z.string()),
  can_send_questionnaire_link: z.boolean(),
  tax_status_changed_info: taxStatusChangedInfo.nullable(),
  history: z.array(agreementHistoryEntry),
});

export const readAgreementDetail = agreementDetailResponse.readonly();

export type ReadAgreementDetail = z.infer<typeof readAgreementDetail>;
export type TaxStatusChangedInfo = z.infer<typeof taxStatusChangedInfo>;
